import { EVENTS, TEST_IDS } from '@va/constants';
import { useTranslate } from '@va/localization';
import { Button, GrayGradientDivider, ModalHeader, Paragraph } from '@va/ui/design-system';
import { ModalWrapper } from '@va/util/components';
import { EventBus } from '@va/util/misc';
import { UnregisterCallback } from 'history';
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router';

type GuardPayload = {
  when: boolean;
  title?: string;
  message?: string;
  id?: string;
};

type GuardsMap = Map<string, GuardPayload>;

type RouterWarningContextValue = {
  setGuard: (id: string, payload: GuardPayload) => void;
  removeGuard: (id: string) => void;
};

const RouterWarningContext = createContext<RouterWarningContextValue | null>(null);

export const RouterWarningProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const translate = useTranslate();
  const history = useHistory();

  const [guards, setGuards] = useState<GuardsMap>(() => new Map());

  const [showWarning, setShowWarning] = useState(false);
  const [nextLocation, setNextLocation] = useState<Location | null>(null);

  const displayedTitle = useMemo(() => {
    for (const [, v] of guards) {
      if (v.when && v.title) return v.title;
    }
    return translate('all.warningModal.title');
  }, [guards, translate]);

  const displayedMessage = useMemo(() => {
    for (const [, v] of guards) {
      if (v.when && v.message) return v.message;
    }
    return translate('all.warningModal.unsavedChangesText');
  }, [guards, translate]);

  const hasActiveGuards = useMemo(() => {
    for (const [_k, v] of guards) {
      if (v.when) {
        return true;
      }
    }
    return false;
  }, [guards]);

  const unblockRef = useRef<UnregisterCallback | null>(null);

  const blockHistory = useCallback(() => {
    if (unblockRef.current) return;
    unblockRef.current = history?.block((tx: any) => {
      const maybeLocation: Location | undefined = typeof tx === 'string' ? undefined : tx;

      if (hasActiveGuards) {
        if (maybeLocation) setNextLocation(maybeLocation);
        setShowWarning(true);
        return false;
      }
      return;
    });
  }, [history, hasActiveGuards]);

  const unblockHistory = useCallback(() => {
    unblockRef.current?.();
    unblockRef.current = null;
  }, []);

  useEffect(() => {
    if (hasActiveGuards) {
      blockHistory();
    } else {
      if (unblockRef.current) {
        unblockHistory();
      }
    }
    return () => {
      if (unblockRef.current) unblockHistory();
    };
  }, [hasActiveGuards, blockHistory, unblockHistory]);

  useEffect(() => {
    const listener = EventBus.addListener(EVENTS.skipUnsavedChangesWarning, (callback: () => void) => {
      const tempUnblock = history.block(() => {});
      tempUnblock();
      callback?.();

      if (hasActiveGuards) blockHistory();
    });
    return () => {
      listener.removeListener();
    };
  }, [history, hasActiveGuards, blockHistory]);

  const setGuard = useCallback((id: string, payload: GuardPayload) => {
    setGuards((prev) => {
      const next = new Map(prev);
      next.set(id, payload);
      return next;
    });
  }, []);

  const removeGuard = useCallback((id: string) => {
    setGuards((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const handleCancel = useCallback(() => {
    setShowWarning(false);
    setNextLocation(null);
  }, []);

  const handleContinue = useCallback(() => {
    const target = nextLocation;
    setShowWarning(false);
    setNextLocation(null);

    unblockHistory();
    if (target) {
      history.push(target.pathname + (target.search || '') + (target.hash || ''));
    }
    if (hasActiveGuards) {
      blockHistory();
    }
  }, [blockHistory, hasActiveGuards, history, nextLocation, unblockHistory]);

  const value = useMemo<RouterWarningContextValue>(() => ({ setGuard, removeGuard }), [setGuard, removeGuard]);

  return (
    <RouterWarningContext.Provider value={value}>
      {children}

      <ModalWrapper closeModal={handleCancel} isModalOpen={showWarning}>
        <div className='max-w-600'>
          <ModalHeader title={displayedTitle} onClose={handleCancel} />
          <Paragraph className='px-5 py-3'>{displayedMessage}</Paragraph>
          <GrayGradientDivider />
          <div className='flex flex-row justify-end p-3 gap-3'>
            <Button
              className='rounded-12 flex-grow'
              color='tertiary'
              text={translate('button.cancel')}
              onClick={handleCancel}
              data-testid={TEST_IDS.generic.buttons.modalCancel}
            />
            <Button
              className='rounded-12 flex-grow text-red-pure'
              color='tertiary'
              text={translate('button.continue')}
              onClick={handleContinue}
              data-testid={TEST_IDS.generic.buttons.modalApply}
            />
          </div>
        </div>
      </ModalWrapper>
    </RouterWarningContext.Provider>
  );
};

export const useRouterWarningContext = () => {
  const ctx = useContext(RouterWarningContext);
  if (!ctx) throw new Error('useRouterWarningContext must be used within RouterWarningProvider');
  return ctx;
};
